import { isEmpty } from 'ramda';
import { routerReducer, RouterState } from 'react-router-redux';
import { combineReducers } from 'redux';
import { FormStateMap, reducer as formReducer } from 'redux-form';
import { combineEpics } from 'redux-observable';
import { createSelector } from 'reselect';
import { lineChartColors, MAX_VISIBLE_DEBTS } from '../components/shared/constants';
import { Debt } from '../models/debt';

import { SUCCESS_CONTACT_SUBMIT } from './actions';
import { authEpic, authReducer, AuthState } from './auth';
import { billingEpic, billingReducer, BillingState } from './billing';
import {
  debtEpic,
  debtReducer,
  DebtState,
  getAllDebts,
  getAllDebtsData,
  getDebtState,
} from './debt';
import { errorReducer, ErrorState } from './error';
import { layoutReducer, LayoutState } from './layout';
import { mxEpic, mxReducer, MxState } from './mx';
import { onboardEpic, onboardReducer, OnboardState } from './onboard/onboard';
import { userEpic, userReducer, UserState } from './user';
import { payoffDebtEpic, payoffDebtReducer, PayoffDebtState } from './debtPayoff';
import { getDebtIdFromPathname, getHistoryFromDebts } from './util';

export interface AscendState {
  auth: AuthState;
  billing: BillingState;
  debt: DebtState;
  error: ErrorState;
  form: FormStateMap;
  layout: LayoutState;
  mx: MxState;
  onboard: OnboardState;
  payoffDebt: PayoffDebtState;
  router: RouterState;
  user: UserState;
}

export const rootReducer = combineReducers({
  auth: authReducer,
  billing: billingReducer,
  debt: debtReducer,
  error: errorReducer,
  form: formReducer.plugin({
    addTransactionForm: (state, action) => {
      switch (action.type) {
        // case SUCCESS_CONTACT_SUBMIT:
        //   return { ...state, values: { ...state.values, message: undefined } }; // Clear contact form on successful submit
        default:
          // {
          //     "registeredFields": {
          //         "description": {
          //             "name": "description",
          //             "type": "Field",
          //             "count": 1
          //         },
          //         "amount": {
          //             "name": "amount",
          //             "type": "Field",
          //             "count": 1
          //         },
          //         "date": {
          //             "name": "date",
          //             "type": "Field",
          //             "count": 1
          //         },
          //         "category": {
          //             "name": "category",
          //             "type": "Field",
          //             "count": 1
          //         },
          //         "type": {
          //             "name": "type",
          //             "type": "Field",
          //             "count": 1
          //         }
          //     },
          //     "fields": {
          //         "type": {
          //             "visited": true,
          //             "touched": true
          //         },
          //         "category": {
          //             "visited": true,
          //             "touched": true
          //         },
          //         "date": {
          //             "visited": true
          //         },
          //         "amount": {
          //             "visited": true,
          //             "touched": true
          //         },
          //         "description": {
          //             "visited": true,
          //             "touched": true
          //         }
          //     },
          //     "values": {
          //         "type": "CREDIT",
          //         "category": "Financial",
          //         "date": "2023-11-16T23:33:00.000Z",
          //         "amount": 121,
          //         "description": "foobar food"
          //     },
          //     "anyTouched": true
          // }
          return state;
      }
    },
    editTransactionForm: (state, action) => {
      switch (action.type) {
        default:
          return state;
      }
    },
    contactForm: (state, action) => {
      switch (action.type) {
        case SUCCESS_CONTACT_SUBMIT:
          return { ...state, values: { ...state.values, message: undefined } }; // Clear contact form on successful submit
        default:
          return state;
      }
    },
  }),
  layout: layoutReducer,
  mx: mxReducer,
  onboard: onboardReducer,
  payoffDebt: payoffDebtReducer,
  router: routerReducer,
  user: userReducer,
});

export const rootEpic = combineEpics(
  authEpic,
  billingEpic,
  debtEpic,
  mxEpic,
  onboardEpic,
  payoffDebtEpic,
  userEpic
);

// Common Selectors
export const getRouterState = (state: AscendState) => state.router;
export const getCurrentDebt = createSelector(
  getRouterState,
  getDebtState,
  (routerState, debtState) => {
    const location = routerState.location;
    const id = getDebtIdFromPathname(location!.pathname);
    if (id && debtState.data[id]) {
      return debtState.data[id];
    } else {
      return { loading: true, data: {} as Debt };
    }
  }
);
export const getLayout = (state: AscendState) => state.layout;
export const getCurrentDebtHistory = createSelector(getCurrentDebt, getLayout, (debt, layout) => {
  if (!isEmpty(debt.data)) {
    return getHistoryFromDebts([debt.data], layout.isMobile);
  } else {
    return [];
  }
});

export const getDebtHistory = createSelector(getAllDebtsData, getLayout, (debts: Debt[], layout) =>
  getHistoryFromDebts(debts, layout.isMobile)
);

export const getDebtChartMetadata = createSelector(getAllDebts, getLayout, (debts, layout) => {
  if (debts.length > MAX_VISIBLE_DEBTS && layout.isMobile) {
    return [{ name: 'Average', color: lineChartColors[0] }];
  } else {
    return debts.map((debt) => ({ name: debt.data.name, color: debt.color }));
  }
});
